import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { ReactComponent as TotalHoursIcon } from 'assets/image/dashboard/total-hours.svg';
import { ReactComponent as ClockIcon } from 'assets/image/dashboard/clock.svg';
import { ReactComponent as CalendarIcon } from 'assets/image/dashboard/calendar-badge.svg';
import { ReactComponent as VacanciesIcon } from 'assets/image/dashboard/vacancies.svg';

import { ReactComponent as RingSvg } from 'assets/image/dashboard/ring-icon.svg';
import { ReactComponent as CircleArrowButton } from 'assets/image/dashboard/circle-arrow-button.svg';

import { urls } from 'core/appUrls';
import { OPPORTUNITIES_TABS } from 'core/constants';
import { getRedirectUrlWithTabHelper } from '../helpers';

import { IOrganisationDashboardByUserResponse } from '@joc/api-gateway/lib/api-client';
import { formatInteger, formatNumber } from 'core/functions/formatNumber';
import { DashboardFiltersType } from '../index';

import ButtonImage from 'shared/components/Buttons/ButtonImage';

import styles from './SmartGroupStatistics.module.scss';

export type SmartGroupStatisticsType = Pick<
	IOrganisationDashboardByUserResponse,
	| 'opportunitiesInPeriod'
	| 'hoursInPeriod'
	| 'opportunitiesPending'
	| 'opportunitiesUpcoming'
	| 'opportunitiesVacancies'
>;

type SmartGroupStatisticsProps = {
	statistics: SmartGroupStatisticsType;
	setFiltersOpen: Dispatch<SetStateAction<boolean>>;
	filters: DashboardFiltersType;
	setFilters: Dispatch<SetStateAction<DashboardFiltersType>>;
};

const SmartGroupStatistics: FC<SmartGroupStatisticsProps> = ({ statistics, setFiltersOpen, filters, setFilters }) => {
	const opportunitiesTitle = 'Opportunities';
	const hoursTitle = 'Hours';
	const oppsPendingApprovalTitle = 'Opps Pending Approval';
	const oppsUpcomingTitle = 'Opps Upcoming';
	const vacanciesTitle = 'Vacancies';

	return (
		<section className={styles.smartGroupStatistics}>
			<div className={styles.smartGroupStatistics__filters}>
				<div
					className={cx(
						styles.smartGroupStatistics__filters_item,
						styles.smartGroupStatistics__filters_item_end
					)}
				>
					<ButtonImage
						clickHandler={() => setFiltersOpen(prevState => !prevState)}
						viewStyle="filter"
						title="Filters"
					/>
					<span className={styles.smartGroupStatistics__filters_item}>Filters</span>
				</div>
			</div>

			<div className={styles.smartGroupStatistics_items}>
				<div className={styles.smartGroupStatistics__item}>
					<RingSvg className={styles.smartGroupStatistics__item_icon} />
					<div className={styles.smartGroupStatistics__item_content}>
						<span className={styles.smartGroupStatistics__item_content_counter}>
							{formatInteger(statistics.opportunitiesInPeriod)}
						</span>
						<span className={styles.smartGroupStatistics__item_content_title}>{opportunitiesTitle}</span>
					</div>
					<Link
						to={getRedirectUrlWithTabHelper(OPPORTUNITIES_TABS.ALL)}
						className={styles.smartGroupStatistics__item_link}
					>
						<CircleArrowButton title={opportunitiesTitle} />
					</Link>
				</div>
				<div className={styles.smartGroupStatistics__item}>
					<TotalHoursIcon className={styles.smartGroupStatistics__item_icon} />
					<div className={styles.smartGroupStatistics__item_content}>
						<span className={styles.smartGroupStatistics__item_content_counter}>
							{formatNumber(statistics.hoursInPeriod)}
						</span>
						<span className={styles.smartGroupStatistics__item_content_title}>{hoursTitle}</span>
					</div>
					<Link to={urls.supperAdminVolList} className={styles.smartGroupStatistics__item_link}>
						<CircleArrowButton title={hoursTitle} />
					</Link>
				</div>
				<div className={styles.smartGroupStatistics__item}>
					<ClockIcon className={styles.smartGroupStatistics__item_icon} />
					<div className={styles.smartGroupStatistics__item_content}>
						<span className={styles.smartGroupStatistics__item_content_counter}>
							{formatInteger(statistics.opportunitiesPending)}
						</span>
						<span className={styles.smartGroupStatistics__item_content_title}>
							{oppsPendingApprovalTitle}
						</span>
					</div>
					<Link to={`${urls.supperAdminOppoList}?manual`} className={styles.smartGroupStatistics__item_link}>
						<CircleArrowButton title={oppsPendingApprovalTitle} />
					</Link>
				</div>
				<div className={styles.smartGroupStatistics__item}>
					<CalendarIcon className={styles.smartGroupStatistics__item_icon} />
					<div className={styles.smartGroupStatistics__item_content}>
						<span className={styles.smartGroupStatistics__item_content_counter}>
							{formatInteger(statistics.opportunitiesUpcoming)}
						</span>
						<span className={styles.smartGroupStatistics__item_content_title}>{oppsUpcomingTitle}</span>
					</div>
					<Link
						to={getRedirectUrlWithTabHelper(OPPORTUNITIES_TABS.UPCOMING)}
						className={styles.smartGroupStatistics__item_link}
					>
						<CircleArrowButton title={oppsUpcomingTitle} />
					</Link>
				</div>
				<div className={styles.smartGroupStatistics__item}>
					<VacanciesIcon className={styles.smartGroupStatistics__item_icon} />
					<div className={styles.smartGroupStatistics__item_content}>
						<span className={styles.smartGroupStatistics__item_content_counter}>
							{formatInteger(statistics.opportunitiesVacancies)}
						</span>
						<span className={styles.smartGroupStatistics__item_content_title}>{vacanciesTitle}</span>
					</div>
					<Link
						to={getRedirectUrlWithTabHelper(OPPORTUNITIES_TABS.VACANCIES)}
						className={styles.smartGroupStatistics__item_link}
					>
						<CircleArrowButton title={vacanciesTitle} />
					</Link>
				</div>
			</div>
		</section>
	);
};

export default SmartGroupStatistics;
